import { baseApi } from "./baseApi";
import type { ScopeFilterParams } from "../../../shared/types/accessScope";

export interface EmailAttachment {
  content: string;
  filename: string;
  type: string;
  disposition: "attachment";
}

export interface SendEmailData {
  to: string;
  from: string;
  subject: string;
  body: string;
  applicantId?: string;
  templateId?: string;
  signatureId?: string;
  cc?: string[];
  bcc?: string[];
  attachments?: EmailAttachment[];
  threadId?: string;
  inReplyToMessageId?: string;
}

export interface SendSmsData {
  to: string;
  body: string;
  applicantId?: string;
}

export interface EmailAttachmentRef {
  id: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
}

export type EmailDeliveryStatus =
  | "sent"
  | "delivered"
  | "opened"
  | "bounced"
  | "dropped"
  | "spam_report"
  | "failed";

export interface EmailHistoryItem {
  id: string;
  notificationType: "email" | "stage_auto_email" | "sms" | "sms_inbound" | "sms_bot_reply";
  direction?: "outbound" | "inbound";
  toEmail: string | null;
  toPhone: string | null;
  subject: string;
  body: string;
  status: string;
  sentAt: string;
  threadId?: string;
  messageId?: string;
  senderEmail?: string;
  senderName?: string;
  attachments?: EmailAttachmentRef[];
  // Phase 7 — delivery status (WhatsApp-style badges)
  deliveryStatus?: EmailDeliveryStatus | null;
  deliveredAt?: string | null;
  openedAt?: string | null;
  bouncedAt?: string | null;
  droppedAt?: string | null;
  spamReportedAt?: string | null;
  bounceReason?: string | null;
  isStarred?: boolean;
  metadata?: {
    fromEmail?: string;
    fromPhone?: string;
    templateId?: string;
    sentBy?: string;
    cc?: string[];
    bcc?: string[];
    autoNotification?: boolean;
    stageName?: string;
  };
}

/** One applicant in a bulk email. Name and email are read from the application when it is sent. */
export interface BulkEmailRecipientInput {
  applicantId: string;
  /** A value given here wins over the application's own, even when null. */
  mergeValues?: { jobTitle?: string | null };
}

export interface StartBulkEmailData {
  /** Chosen by the client, so a retried request cannot start a second send. */
  batchId: string;
  recipients: BulkEmailRecipientInput[];
  from: string;
  subject: string;
  body: string;
  templateId?: string;
  signatureId?: string;
  cc?: string[];
  bcc?: string[];
  attachments?: EmailAttachment[];
}

export interface StartBulkEmailResponse {
  batchId: string;
  total: number;
  /** False when the server's queue is down: the client drives the send with processBulkEmail. */
  queued: boolean;
}

export type BulkEmailRecipientStatus = "pending" | "sending" | "sent" | "failed" | "skipped";

export interface BulkEmailBatch {
  batchId: string;
  status: "pending" | "processing" | "completed";
  total: number;
  counts: Record<BulkEmailRecipientStatus, number>;
  createdAt: string;
  completedAt: string | null;
  problems: Array<{
    applicantId: string;
    fullName: string | null;
    email: string | null;
    status: "failed" | "skipped";
    error: string | null;
    /** The send was interrupted: the candidate may already have this email. */
    outcomeUnknown: boolean;
  }>;
}

interface SendEmailResponse {
  success: boolean;
  message: string;
}

interface EmailHistoryResponse {
  emails: EmailHistoryItem[];
}

export type InboxChannel = "email" | "sms";
export type InboxFilter = "all" | "unread" | "needs_reply" | "failed" | "starred";

export interface InboxThread {
  applicantId: string;
  applicantName: string;
  applicantEmail: string | null;
  applicantPhone: string | null;
  lastSubject: string;
  lastPreview: string;
  lastAt: string | null;
  lastDirection: "inbound" | "outbound";
  lastDeliveryStatus: EmailDeliveryStatus | null;
  unreadCount: number;
  isStarred: boolean;
  channel: InboxChannel;
}

interface InboxThreadsResponse {
  threads: InboxThread[];
  total: number;
  nextOffset: number | null;
}

interface InboxCountsResponse {
  email: number;
  sms: number;
}

export const emailApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    sendEmail: builder.mutation<SendEmailResponse, { instanceId: string; data: SendEmailData }>({
      query: ({ instanceId, data }) => ({
        url: `/email/send?instanceId=${instanceId}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Applicants", "EmailHistory"],
    }),

    // Bulk email is sent server-side. None of these invalidate "Applicants":
    // the tracker does that once, when the batch finishes, instead of the list
    // refetching after every recipient.
    startBulkEmail: builder.mutation<
      StartBulkEmailResponse,
      { instanceId: string; data: StartBulkEmailData }
    >({
      query: ({ instanceId, data }) => ({
        url: `/email/bulk?instanceId=${instanceId}`,
        method: "POST",
        body: data,
      }),
    }),

    getBulkEmailBatch: builder.query<BulkEmailBatch, { instanceId: string; batchId: string }>({
      query: ({ instanceId, batchId }) => ({
        url: `/email/bulk/${batchId}?instanceId=${instanceId}`,
        method: "GET",
      }),
      keepUnusedDataFor: 30,
    }),

    processBulkEmail: builder.mutation<BulkEmailBatch, { instanceId: string; batchId: string }>({
      query: ({ instanceId, batchId }) => ({
        url: `/email/bulk/${batchId}/process?instanceId=${instanceId}`,
        method: "POST",
      }),
    }),

    retryBulkEmail: builder.mutation<
      { batchId: string; requeued: number; queued: boolean },
      { instanceId: string; batchId: string; includeUnknown?: boolean }
    >({
      query: ({ instanceId, batchId, includeUnknown }) => ({
        url: `/email/bulk/${batchId}/retry?instanceId=${instanceId}`,
        method: "POST",
        body: { includeUnknown: includeUnknown === true },
      }),
    }),

    sendSms: builder.mutation<SendEmailResponse, { instanceId: string; data: SendSmsData }>({
      query: ({ instanceId, data }) => ({
        url: `/email/sms/send?instanceId=${instanceId}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Applicants", "EmailHistory"],
    }),

    generateEmail: builder.mutation<
      { subject: string; body: string },
      { recipientName: string; jobTitle?: string; purpose: string; instructions?: string }
    >({
      query: (body) => ({
        url: "/email/generate",
        method: "POST",
        body,
      }),
    }),

    getEmailHistory: builder.query<
      EmailHistoryResponse,
      { instanceId: string; applicantId: string; hideInboxHidden?: boolean }
    >({
      query: ({ instanceId, applicantId, hideInboxHidden }) => {
        const params = new URLSearchParams({ instanceId });
        if (hideInboxHidden) params.set("hideInboxHidden", "true");
        return {
          url: `/email/history/${applicantId}?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["EmailHistory"],
    }),

    getEmailAttachmentDownloadUrl: builder.query<
      { downloadUrl: string; fileName: string; mimeType: string | null },
      { instanceId: string; attachmentId: string }
    >({
      query: ({ instanceId, attachmentId }) => ({
        url: `/email/attachments/${attachmentId}/download?instanceId=${instanceId}`,
        method: "GET",
      }),
    }),

    getInboxThreads: builder.query<
      InboxThreadsResponse,
      {
        instanceId: string;
        channel: InboxChannel;
        filter: InboxFilter;
        search?: string;
        limit?: number;
        offset?: number;
      } & ScopeFilterParams
    >({
      query: ({
        instanceId,
        channel,
        filter,
        search,
        limit = 30,
        offset = 0,
        locationId,
        groupId,
        departmentId,
      }) => {
        const params = new URLSearchParams({
          instanceId,
          channel,
          filter,
          limit: String(limit),
          offset: String(offset),
        });
        if (search && search.trim().length > 0) params.set("search", search.trim());
        // The header's Location choice (D27, D30): a thread belongs to a candidate
        // whose application names the job that carries the community.
        if (locationId !== undefined) params.set("locationId", String(locationId));
        if (groupId) params.set("groupId", groupId);
        if (departmentId) params.set("departmentId", departmentId);
        return {
          url: `/email/inbox?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: ["EmailHistory"],
    }),

    getInboxCounts: builder.query<InboxCountsResponse, { instanceId: string } & ScopeFilterParams>({
      query: ({ instanceId, locationId, groupId, departmentId }) => {
        const params = new URLSearchParams({ instanceId });
        // Counted over the same slice the list shows (D30).
        if (locationId !== undefined) params.set("locationId", String(locationId));
        if (groupId) params.set("groupId", groupId);
        if (departmentId) params.set("departmentId", departmentId);
        return { url: `/email/inbox/counts?${params.toString()}`, method: "GET" };
      },
      providesTags: ["EmailHistory"],
    }),

    markMessageRead: builder.mutation<
      { success: boolean },
      { instanceId: string; messageId: string; read: boolean }
    >({
      query: ({ instanceId, messageId, read }) => ({
        url: `/email/messages/${messageId}/read?instanceId=${instanceId}`,
        method: "POST",
        body: { read },
      }),
      invalidatesTags: ["EmailHistory"],
    }),

    toggleMessageStar: builder.mutation<
      { success: boolean },
      { instanceId: string; messageId: string; starred: boolean }
    >({
      query: ({ instanceId, messageId, starred }) => ({
        url: `/email/messages/${messageId}/star?instanceId=${instanceId}`,
        method: "POST",
        body: { starred },
      }),
      invalidatesTags: ["EmailHistory"],
    }),

    markThreadRead: builder.mutation<
      { success: boolean },
      { instanceId: string; applicantId: string; channel: InboxChannel }
    >({
      query: ({ instanceId, applicantId, channel }) => ({
        url: `/email/inbox/${applicantId}/mark-all-read?instanceId=${instanceId}&channel=${channel}`,
        method: "POST",
      }),
      invalidatesTags: ["EmailHistory"],
    }),

    deleteMessage: builder.mutation<
      { success: boolean },
      { instanceId: string; messageId: string }
    >({
      query: ({ instanceId, messageId }) => ({
        url: `/email/messages/${messageId}?instanceId=${instanceId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EmailHistory"],
    }),

    deleteThread: builder.mutation<
      { success: boolean },
      { instanceId: string; applicantId: string; channel: InboxChannel }
    >({
      query: ({ instanceId, applicantId, channel }) => ({
        url: `/email/inbox/${applicantId}?instanceId=${instanceId}&channel=${channel}`,
        method: "DELETE",
      }),
      invalidatesTags: ["EmailHistory"],
    }),

    cleanupInbox: builder.mutation<
      { success: boolean; hidden: number },
      { instanceId: string; channel: InboxChannel; days: number }
    >({
      query: ({ instanceId, channel, days }) => ({
        url: `/email/inbox/cleanup?instanceId=${instanceId}`,
        method: "POST",
        body: { channel, days },
      }),
      invalidatesTags: ["EmailHistory"],
    }),
  }),
});

export const {
  useSendEmailMutation,
  useStartBulkEmailMutation,
  useGetBulkEmailBatchQuery,
  useProcessBulkEmailMutation,
  useRetryBulkEmailMutation,
  useSendSmsMutation,
  useGenerateEmailMutation,
  useGetEmailHistoryQuery,
  useLazyGetEmailAttachmentDownloadUrlQuery,
  useGetInboxThreadsQuery,
  // Used to pull the pages after the first one as the thread list is
  // scrolled; the first page stays on the polled query above.
  useLazyGetInboxThreadsQuery,
  useGetInboxCountsQuery,
  useMarkMessageReadMutation,
  useToggleMessageStarMutation,
  useMarkThreadReadMutation,
  useDeleteMessageMutation,
  useDeleteThreadMutation,
  useCleanupInboxMutation,
} = emailApi;
